
import { format } from "date-fns";
import { Edit2, Download, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Database } from "@/integrations/supabase/types";
import { getDocumentDisplayTitle } from "@/utils/document-formatter";
import { generatePDF } from "@/utils/pdf-generator";
import { useToast } from "@/hooks/use-toast";

type Document = Database["public"]["Tables"]["documents"]["Row"];

interface DocumentCardProps {
  doc: Document;
  onNavigate: (id: string) => void;
  onDownload: (doc: Document) => void;
  onDelete: (id: string) => void;
  downloadingId: string | null;
}

export function DocumentCard({ doc, onNavigate, onDownload, onDelete, downloadingId }: DocumentCardProps) {
  const { toast } = useToast();
  
  const handleDownload = async () => {
    try {
      onDownload(doc);
      await generatePDF(doc);
      toast({
        title: "Success",
        description: "Document downloaded successfully."
      });
    } catch (error: any) {
      console.error("Error downloading document:", error);
      toast({
        title: "Error",
        description: "Failed to download document. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow h-full flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-gray-900 line-clamp-2">
          {doc.title || getDocumentDisplayTitle(doc.type)}
        </CardTitle>
        <CardDescription className="text-sm text-gray-600">
          Last updated: {format(new Date(doc.updated_at), "MMM d, yyyy, h:mm a")}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0 mt-auto">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNavigate(doc.id)}
            className="flex-1 min-w-0"
          >
            <Edit2 className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={downloadingId === doc.id}
            className="flex-1 min-w-0"
          >
            <Download className="h-4 w-4 mr-2" />
            {downloadingId === doc.id ? 'Downloading...' : 'Download'}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" className="w-full mt-2">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Document</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this document? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDelete(doc.id)}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}
